/**
 * Jurídico: reajustes contratuais e ocorrências de inadimplência e descumprimento (ADR 0057).
 * Registro jurídico: não altera dados financeiros oficiais. O percentual do índice é informado.
 */
import { z } from 'zod';
import { fromScaled, toScaled } from './money.ts';

export const ADJUSTMENT_INDEXES = ['ipca', 'igpm', 'inpc', 'fixed', 'other'] as const;
export const BREACH_PARTIES = ['loot', 'counterparty'] as const;
export const BREACH_KINDS = ['payment', 'delivery', 'obligation', 'other'] as const;
export const BREACH_STATUSES = ['open', 'in_treatment', 'resolved', 'closed'] as const;

const day = z.iso.date();
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
/** Percentual com até quatro casas, entre -100 e 1000 (ex.: 4.5 ou -1.25). */
const rate = z
  .string()
  .trim()
  .regex(/^-?\d{1,4}(\.\d{1,4})?$/, 'Percentual com até quatro casas (ex.: 4.5)')
  .refine((v) => Number(v) >= -100 && Number(v) <= 1000, 'Entre -100 e 1000');
const money = z
  .string()
  .trim()
  .regex(/^\d{1,15}(\.\d{1,4})?$/, 'Valor com até quatro casas')
  .or(z.literal(''))
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional();
const currency = z
  .string()
  .regex(/^[A-Z]{3}$/)
  .or(z.literal(''))
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional();

/** Regra de reajuste do contrato. Sem índice, o contrato não tem reajuste. */
export const contractAdjustmentTermsInput = z
  .object({
    index: z.enum(ADJUSTMENT_INDEXES).nullable(),
    note: optionalText(300),
    baseDate: day.nullable().optional(),
    months: z.number().int().min(1).max(120).nullable().optional(),
    nextDate: day.nullable().optional(),
  })
  .refine((v) => !v.index || v.months != null, {
    path: ['months'],
    message: 'Informe de quantos em quantos meses o reajuste acontece',
  })
  .refine((v) => !v.index || !['fixed', 'other'].includes(v.index) || !!v.note, {
    path: ['note'],
    message: 'Descreva o índice ou o percentual combinado',
  });

/** Reajuste aplicado: data, percentual informado e observação. */
export const contractAdjustmentApplyInput = z.object({
  effectiveDate: day,
  rate,
  note: optionalText(2000),
});

export const contractBreachInput = z
  .object({
    party: z.enum(BREACH_PARTIES),
    kind: z.enum(BREACH_KINDS),
    description: z.string().trim().min(5).max(4000),
    occurredOn: day,
    amount: money,
    currency,
    status: z.enum(BREACH_STATUSES).default('open'),
    resolution: optionalText(4000),
    resolvedOn: day.nullable().optional(),
  })
  .refine((v) => (v.amount == null) === (v.currency == null), {
    path: ['currency'],
    message: 'Informe o valor e a moeda juntos',
  })
  .refine((v) => !['resolved', 'closed'].includes(v.status) || (!!v.resolution && !!v.resolvedOn), {
    path: ['resolution'],
    message: 'Ao encerrar, informe as providências e a data',
  });

/**
 * Novo valor depois do reajuste: valor × (1 + percentual / 100), arredondado na quarta casa
 * (metade para longe do zero). Sem ponto flutuante.
 */
export function applyAdjustmentRate(amount: string, ratePercent: string): string {
  const base = toScaled(amount);
  const r = toScaled(ratePercent);
  const num = base * r;
  const den = 100n * 10_000n;
  const half = den / 2n;
  const delta = num >= 0n ? (num + half) / den : -((-num + half) / den);
  return fromScaled(base + delta);
}

/** Próxima data de reajuste: a data informada mais o período, em meses. */
export function addMonths(date: string, months: number): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const target = new Date(Date.UTC(y, m - 1 + months, 1));
  const last = new Date(
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0),
  ).getUTCDate();
  target.setUTCDate(Math.min(d, last));
  return target.toISOString().slice(0, 10);
}

/**
 * Próxima data de reajuste depois de aplicar um (auditoria de 27/10, Onda 2): conta pelos
 * aniversários da data-base (ou da próxima data já prevista), e não pela data em que o reajuste
 * foi lançado. Sem nenhuma das duas, vale a data do reajuste mais o período.
 */
export function nextAdjustmentAfter(
  effectiveDate: string,
  months: number,
  baseDate: string | null,
  scheduled: string | null,
): string {
  let next = scheduled ?? (baseDate ? addMonths(baseDate, months) : null);
  if (!next) return addMonths(effectiveDate, months);
  const anchor = next;
  // Pelo número de períodos desde a âncora, para não acumular o ajuste de fim de mês.
  for (let k = 1; next <= effectiveDate && k < 1200; k++) next = addMonths(anchor, months * k);
  return next;
}
